import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { RootState } from "store/rootReducer";

import { EntityKind } from "models/entityKind";

import { exportFieldGroupData } from "components/user-defined-columns/queries/useFieldGroupDataQuery";

interface UseFieldGroupDownloadParams {
  fieldGroup: string;
  entityKind: EntityKind;
  isOrgDefinedColumn: boolean;
  isProjectDefinedColumn: boolean;
}

interface UseFieldGroupDownloadReturn {
  isDownloading: boolean;
  handleDownload: (e: React.MouseEvent) => Promise<void>;
}

export function useFieldGroupDownload({
  fieldGroup,
  entityKind,
  isOrgDefinedColumn,
  isProjectDefinedColumn
}: UseFieldGroupDownloadParams): UseFieldGroupDownloadReturn {
  const [isDownloading, setIsDownloading] = useState(false);

  // Get filter IDs from Redux
  const projectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const wellFilterId = useSelector((state: RootState) => state.filter.filterId);
  const facilityFilterId = useSelector(
    (state: RootState) => state.facilityFilter.filterId
  );

  const filterId =
    entityKind === EntityKind.Well ? wellFilterId?.id : facilityFilterId?.id;
  const effectiveProjectId = isProjectDefinedColumn ? projectId : null;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!filterId) {
      toast.error("No filter selected. Please load wells first.");
      return;
    }

    setIsDownloading(true);
    try {
      await exportFieldGroupData(
        fieldGroup,
        entityKind,
        isOrgDefinedColumn,
        filterId,
        effectiveProjectId
      );

      toast.success(`Downloaded ${fieldGroup} data.`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to download field group data:", error);
      toast.error("Failed to download field group data. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    handleDownload
  };
}

export default useFieldGroupDownload;
